"use client";

import { useRouter } from 'next/navigation';
import Form, { StringInput, NumberInput, EmailInput, WebsiteInput, DropdownInput, TextareaInput, HiddenInput } from "@/components/forms";
import { useGeoIP } from "@/providers/geoip";
import { ProposalForm as schema } from "@/types";

type Props = {}

export const ProposalForm: React.FC<Props> = ({ }) => {
    const { geoInfo, isLoading } = useGeoIP();
    const router = useRouter();

    return (
        <Form code="proposal" schema={schema} title="Get Your Tailored Proposal"
            description="Please fill out the form below to receive a personalized proposal tailored to your needs."
            buttonName="Get a Proposal"
            initialValues={{ lines: 1, monthlyMissedCalls: 0, agents: 1, agentHourlyRate: 10, monthlyCalls: 600, monthlyMinutes: 2400, cpl: 0, averageServicePrice: 35, primaryGoal: "" }}
            onSubmitted={(data) => {
                router.push('/proposal/' + data.id);
            }}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StringInput name="firstName" label="What is your first name?" description="required" placeholder="e.g. John" />
                <StringInput name="lastName" label="What is your last name?" description="required" placeholder="e.g. Doe" />
                <EmailInput name="email" label="What is your email address?" description="required" placeholder="e.g. john.doe@example.com" />
                <StringInput name="company" label="What is your company's name?" description="required" placeholder="e.g. Acme Inc." />
            </div>
            <WebsiteInput name="website" label="Company website" description="required" placeholder="e.g. acme.com" />
            <p className="text-md font-semibold">Call Volume & Infrastructure</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumberInput name="agents" label="How many agents do you have?" description="required" placeholder="e.g. 5" />
                <NumberInput name="agentHourlyRate" label="What is your agent's hourly rate?" description="required" placeholder="e.g. 15" />
                <NumberInput name="lines" label="How many telephone lines do you have?" description="required" placeholder="e.g. 5" />
                <NumberInput name="monthlyMinutes" label="What is your average monthly call duration (in minutes)?" description="required" placeholder="e.g. 2400" />
                <NumberInput name="monthlyCalls" label="What is your average monthly calls?" description="required" placeholder="e.g. 600" />
                <NumberInput name="monthlyMissedCalls" label="How many calls do you miss per month?" description="required" placeholder="e.g. 500" />
                <NumberInput name="cpl" label="What is your cost per lead (CPL)?" description="required" placeholder="e.g. 15" />
                <NumberInput name="averageServicePrice" label="What is the average price of your service?" description="required" placeholder="e.g. 35" />
            </div>
            <p className="text-md font-semibold">Your Goals</p>
            <DropdownInput name="primaryGoal" label="What is your primary goal for using our service?" description="required" options={{
                "Reduce missed calls and recover leads": "Reduce missed calls and recover leads",
                "Provide 24/7 customer support": "Provide 24/7 customer support",
                "Eliminate customer hold times": "Eliminate customer hold times",
                "Handle high call volume spikes": "Handle high call volume spikes",
                "Reduce support and call center costs": "Reduce support and call center costs",
                "Free up staff from answering routine calls": "Free up staff from answering routine calls",
                "Address staff shortages": "Address staff shortages",
                "Filter spam and irrelevant calls": "Filter spam and irrelevant calls",
                "Automate responses to frequently asked questions": "Automate responses to frequently asked questions",
                "Automate appointment scheduling and booking": "Automate appointment scheduling and booking",
                "Qualify leads before transferring to an agent": "Qualify leads before transferring to an agent",
                "Route calls to the correct department automatically": "Route calls to the correct department automatically",
                "Replace an outdated IVR menu": "Replace an outdated IVR menu",
                "Provide multilingual support": "Provide multilingual support",
                "Automate taking phone orders": "Automate taking phone orders",
                "Other": "Other",
            }} />
            <TextareaInput name="additionalInfo" label="Anything else you'd like to share?" description="optional" placeholder="Please provide any additional information or specific requirements..." />
            {!isLoading && (
                <HiddenInput name="countryCode" value={geoInfo?.country || ""} />
            )}
        </Form>
    );
}